import {
  ComponentProps,
  MinimalFormattedPostData,
  SortOption
} from '@/components/competitorAnalytics/helpers';
import { getDateTimeFormated } from '@/components/utils/dates/utils';
import {
  FACEBOOK,
  INSTAGRAM,
  LINKEDIN_BUSINESS_PAGE,
  LINKEDIN_PERSONAL_ACCOUNT,
  TIKTOK_BUSINESS_PAGE,
  TIKTOK_PERSONAL_ACCOUNT,
  TWITTER,
  YOUTUBE
} from '@/config/constants';
import {
  ComparativeColumn,
  ComparativeRow,
  ComparativeSection,
  DataTable
} from '@/lomavisSharedUi/organisms/cards/LomavisComparisionTableCard1';

type Campaign = {
  uuid: string;
  name: string;
  color: string;
  created: string;
  modified: string;
  logo_url: string;
};

type PublishedPlatform = {
  platform: string;
  created: boolean;
  published: boolean;
  deleted: boolean;
};

export interface PlatformAnalytics {
  impressions?: number;
  reach?: number;
  shares?: number | null;
  comments?: number;
  reactions?: number;
  engagement?: number;
  share_rate?: number;
  comment_to_reaction_ratio?: number | null;
  interactions?: number;
}

export type Post = {
  /** Post unique identifier */
  uuid: string;

  /** Optional text content */
  text?: string;

  /** Campaigns linked to this post */
  campaigns: {
    uuid: string;
    name: string;
    color: string;
  }[];

  /** When the post was actually published */
  publication_date?: string;

  /** Analytics summary */
  analytics: {
    /** Raw JSON data (per platform published state etc.) */
    data_as_json: {
      published: PublishedPlatform[];
      overview?: Record<string, PlatformAnalytics>;
      // you can add overview, summary if needed:
      // overview?: Record<string, any>;
      // summary?: Record<string, any>;
    };

    post_performance_rating?: string;

    /** Aggregated sums (used in calculations) */
    sum_impressions?: number;
    sum_reach?: number;
    sum_reactions?: number;
    sum_comments?: number;
    sum_shares?: number;
    sum_engagement?: number; // sometimes percentage
    sum_interactions?: number;
  };

  /** Optional preview image for UI */
  preview_image_url?: string;

  /** Flags for each platform */
  facebook_post?: boolean;
  instagram_post?: boolean;
  twitter_post?: boolean;
  linkedin_personal_account_post?: boolean;
  linkedin_business_page_post?: boolean;
  youtube_post?: boolean;
  tiktok_personal_account_post?: boolean;
  tiktok_business_page_post?: boolean;
  whatsapp_post?: boolean;
  pinterest_post?: boolean;

  /** Anything else you need to include */
  [key: string]: any;
};

export type CampaignStats = {
  uuid: string;
  name: string;
  created: string;
  color: string;
  logo_url: string;
  postCount?: number;
  dayCount?: number;

  impressions?: number;
  reach?: number;
  reactions?: number;
  comments?: number;
  shares?: number;
  interactions?: number;
  engagement?: number; // as percentage
};

type NumericAnalyticsKeys =
  | 'sum_impressions'
  | 'sum_reach'
  | 'sum_reactions'
  | 'sum_comments'
  | 'sum_shares'
  | 'sum_interactions';

export function getCampaignPostCounts(
  selectedCampaigns: Campaign[],
  posts: Post[]
): CampaignStats[] {
  const countsMap: Record<string, number> = {};

  // Count logic
  posts.forEach((post) => {
    const platformCount =
      post.analytics?.data_as_json?.published?.filter(
        (p) => p.published && !p.deleted
      ).length ?? 0;

    if (!platformCount) return;

    post.campaigns.forEach((c) => {
      if (selectedCampaigns.find((sc) => sc.uuid === c.uuid)) {
        countsMap[c.uuid] = (countsMap[c.uuid] || 0) + platformCount;
      }
    });
  });

  // Return array
  return selectedCampaigns.map((c) => ({
    uuid: c.uuid,
    name: c.name,
    created: c.created,
    color: c.color,
    logo_url: c.logo_url,
    postCount: countsMap[c.uuid] || 0
  }));
}

export function getCampaignDayCounts(
  selectedCampaigns: Campaign[],
  posts: Post[]
): CampaignStats[] {
  const dayTracker: Record<string, Set<string>> = {};

  selectedCampaigns.forEach((c) => {
    dayTracker[c.uuid] = new Set<string>();
  });

  posts.forEach((post) => {
    if (!post.publication_date) return;
    const day = new Date(post.publication_date).toISOString().split('T')[0];

    post.campaigns.forEach((c) => {
      if (selectedCampaigns.find((sc) => sc.uuid === c.uuid)) {
        dayTracker[c.uuid]?.add(day);
      }
    });
  });

  return selectedCampaigns.map((c) => ({
    uuid: c.uuid,
    name: c.name,
    created: c.created,
    color: c.color,
    logo_url: c.logo_url,
    dayCount: dayTracker[c.uuid]?.size || 0
  }));
}

export function getCampaignAnalyticsStats(
  selectedCampaigns: Campaign[],
  posts: Post[]
): CampaignStats[] {
  // Initialize stat containers
  const statsMap: Record<string, CampaignStats> = {};

  selectedCampaigns.forEach((c) => {
    statsMap[c.uuid] = {
      uuid: c.uuid,
      name: c.name,
      created: c.created,
      color: c.color,
      logo_url: c.logo_url,
      impressions: 0,
      reach: 0,
      reactions: 0,
      comments: 0,
      shares: 0,
      interactions: 0
    };
  });

  posts.forEach((post) => {
    const analytics = post?.analytics;
    const {
      sum_impressions = 0,
      sum_reach = 0,
      sum_reactions = 0,
      sum_comments = 0,
      sum_shares = 0,
      sum_interactions = 0
    } = analytics;

    post.campaigns.forEach((c) => {
      if (!statsMap[c.uuid]) return;

      statsMap[c.uuid].impressions! += sum_impressions;
      statsMap[c.uuid].reach! += sum_reach;
      statsMap[c.uuid].reactions! += sum_reactions;
      statsMap[c.uuid].comments! += sum_comments;
      statsMap[c.uuid].shares! += sum_shares;
      statsMap[c.uuid].interactions! += sum_interactions;
    });
  });

  // Calculate engagement (interactions ÷ reach × 100)
  Object.values(statsMap).forEach((stat) => {
    if (stat.reach && stat.reach > 0) {
      stat.engagement = parseFloat(
        ((stat.interactions! / stat.reach) * 100).toFixed(2)
      );
    } else {
      stat.engagement = 0;
    }
  });

  return Object.values(statsMap);
}

function getUsedPlatforms(posts: Post[]): string {
  const allPlatforms = new Set<string>();
  posts.forEach((post) => {
    const published = post.analytics?.data_as_json?.published || [];
    published.forEach((p) => {
      if (p.published && !p.deleted) {
        allPlatforms.add(p.platform);
      }
    });
  });

  return [...allPlatforms].join(', ');
}

function getMostCommonPostTime(posts: Post[], t: any): string {
  const map: Record<string, number> = {};
  posts.forEach((post) => {
    if (!post.publication_date) return;
    const date = new Date(post.publication_date);
    const weekday = t(
      date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase()
    );
    const hour = date.getHours();
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    const key = `${weekday} at ${displayHour}:00 ${ampm}`;
    map[key] = (map[key] || 0) + 1;
  });
  return Object.entries(map).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A';
}

function getLatestPostDate(posts: Post[]): string {
  const dates = posts
    .map((p) => p.publication_date)
    .filter(Boolean)
    .map((d) => new Date(d!));
  if (!dates.length) return 'N/A';
  return getDateTimeFormated(
    new Date(Math.max(...dates.map((d) => d.getTime()))),
    true
  );
}

function getEarliestPostDate(posts: Post[]): string {
  const dates = posts
    .map((p) => p.publication_date)
    .filter(Boolean)
    .map((d) => new Date(d!));
  if (!dates.length) return 'N/A';
  return getDateTimeFormated(
    new Date(Math.min(...dates.map((d) => d.getTime()))),
    true
  );
}

function getMostUsedContentType(posts: Post[]): string {
  const counters = {
    Reel: 0,
    Video: 0,
    'Multi Image': 0,
    'Single Image': 0
  };
  posts.forEach((post) => {
    if (post.content_type_is_reel_post) counters.Reel++;
    if (post.content_type_is_video_post) counters.Video++;
    if (post.content_type_is_multi_image_post) counters['Multi Image']++;
    if (post.content_type_is_single_image_post) counters['Single Image']++;
  });
  const entries = Object.entries(counters).sort((a, b) => b[1] - a[1]);
  return entries[0]?.[1] > 0 ? entries[0][0] : 'N/A';
}
function getMostSuccessfulContentType(posts: Post[]): string {
  const scores: Record<string, number> = {
    Reel: 0,
    Video: 0,
    'Multi Image': 0,
    'Single Image': 0
  };

  posts.forEach((post) => {
    const interactions = post.analytics?.sum_interactions || 0;

    if (post.content_type_is_reel_post) scores['Reel'] += interactions;
    if (post.content_type_is_video_post) scores['Video'] += interactions;
    if (post.content_type_is_multi_image_post)
      scores['Multi Image'] += interactions;
    if (post.content_type_is_single_image_post)
      scores['Single Image'] += interactions;
  });

  const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  return sorted[0]?.[1] > 0 ? sorted[0][0] : 'N/A';
}
function getMostCommonPerformanceEmoji(posts: Post[]): string {
  const emojiCount: Record<string, number> = {};

  posts.forEach((post) => {
    const emoji = post.analytics?.post_performance_rating;
    if (emoji) {
      emojiCount[emoji] = (emojiCount[emoji] || 0) + 1;
    }
  });

  const sorted = Object.entries(emojiCount).sort((a, b) => b[1] - a[1]);
  return sorted[0]?.[0] || '—'; // fallback to '—' if none found
}

function sum(posts: Post[], key: NumericAnalyticsKeys): number {
  return posts.reduce((acc, post) => acc + (post.analytics?.[key] ?? 0), 0);
}

function average(total: number, count: number): string {
  return count > 0 ? (total / count).toFixed(2) : '0';
}
function getPostPlatformCount(post: Post): number {
  return (
    post.analytics?.data_as_json?.published?.filter(
      (p) => p.published && !p.deleted
    ).length ?? 0
  );
}

function getTotalPostCount(posts: Post[]): number {
  return posts.reduce((acc, post) => acc + getPostPlatformCount(post), 0);
}

export function transformCampaignAnalyticsData(
  selectedCampaigns: Campaign[],
  posts: Post[],
  t: any
): DataTable {
  const columns: ComparativeColumn[] = selectedCampaigns.map((campaign) => ({
    title: campaign.name,
    image: campaign.logo_url
  }));
  const emojiRatings = ['😐', '🎉', '🚀'];

  const metricsByCampaign = selectedCampaigns.map((campaign) => {
    const campaignPosts = posts.filter((post) =>
      post.campaigns.some((c) => c.uuid === campaign.uuid)
    );

    const postCount = getTotalPostCount(campaignPosts);

    const mostSuccessfulContentType =
      getMostSuccessfulContentType(campaignPosts);

    const impressions = sum(campaignPosts, 'sum_impressions');
    const reach = sum(campaignPosts, 'sum_reach');
    const reactions = sum(campaignPosts, 'sum_reactions');
    const comments = sum(campaignPosts, 'sum_comments');
    const shares = sum(campaignPosts, 'sum_shares');
    const interactions = sum(campaignPosts, 'sum_interactions');
    const engagementRate =
      reach > 0 ? ((interactions / reach) * 100).toFixed(2) : '0';

    const reels = campaignPosts.filter(
      (p) => p.content_type_is_reel_post
    ).length;
    const videos = campaignPosts.filter(
      (p) => p.content_type_is_video_post
    ).length;
    const multiimage = campaignPosts.filter(
      (p) => p.content_type_is_multi_image_post
    ).length;
    const singleimage = campaignPosts.filter(
      (p) => p.content_type_is_single_image_post
    ).length;

    const performanceRatingCounts: Record<string, number> = {
      '😐': 0,
      '🎉': 0,
      '🚀': 0
    };

    campaignPosts.forEach((post) => {
      const rating = post.analytics?.post_performance_rating;
      if (rating && performanceRatingCounts[rating] !== undefined) {
        performanceRatingCounts[rating]++;
      }
    });

    return {
      postCount,
      duration: `${getEarliestPostDate(campaignPosts)} - ${getLatestPostDate(
        campaignPosts
      )}`,
      usedPlatforms: getUsedPlatforms(campaignPosts),
      mostPostTime: getMostCommonPostTime(campaignPosts, t),
      latestPost: getLatestPostDate(campaignPosts),
      earliestPost: getEarliestPostDate(campaignPosts),
      mostUsedContentType: getMostUsedContentType(campaignPosts),
      mostSuccessfulContentType, // You can fill this logic later
      reels,
      videos,
      multiimage,
      singleimage,
      impressions,
      reach,
      reactions,
      comments,
      shares,
      interactions,
      engagementRate: `${engagementRate}%`,
      avgImpressions: average(impressions, postCount),
      avgReach: average(reach, postCount),
      avgReactions: average(reactions, postCount),
      avgComments: average(comments, postCount),
      avgShares: average(shares, postCount),
      avgInteractions: average(interactions, postCount),
      performanceRatingCounts
    };
  });

  const section = (
    title: string,
    rows: ComparativeRow[]
  ): ComparativeSection => ({
    title,
    rows
  });

  const getRow = (
    title: string,
    key: keyof (typeof metricsByCampaign)[number]
  ): ComparativeRow => ({
    title,
    values: metricsByCampaign.map((m) => {
      const value = m[key];
      if (key === 'usedPlatforms' && Array.isArray(value)) {
        return value.join(', ');
      }
      return typeof value === 'object' ? 'N/A' : value ?? 'N/A';
    }),
    ...(key === 'usedPlatforms' ? { renderAs: 'platform' } : {})
  });

  const getRatingRow = (emoji: string, label: string): ComparativeRow => ({
    title: label,
    values: metricsByCampaign.map(
      (m) => m.performanceRatingCounts?.[emoji] ?? 0
    )
  });

  const dataTable: DataTable = {
    title: t('campaignAnalytics.comparative_table'),
    columns,
    sections: [
      section(t('campaignAnalytics.summary'), [
        getRow(t('campaignAnalytics.usedPlatforms'), 'usedPlatforms'),
        getRow(t('campaignAnalytics.totalPosts'), 'postCount'),
        getRow(t('campaignAnalytics.duration'), 'duration')
      ]),
      section(t('campaignAnalytics.content'), [
        getRow(t('campaignAnalytics.mostPostTime'), 'mostPostTime'),
        getRow(t('campaignAnalytics.latestPost'), 'latestPost'),
        getRow(t('campaignAnalytics.earliestPost'), 'earliestPost'),
        getRow(
          t('campaignAnalytics.mostUsedContentType'),
          'mostUsedContentType'
        ),
        getRow(
          t('campaignAnalytics.mostSuccessfulContentType'),
          'mostSuccessfulContentType'
        ),
        getRow(t('campaignAnalytics.reels'), 'reels'),
        getRow(t('campaignAnalytics.videos'), 'videos'),
        getRow(t('campaignAnalytics.multiImage'), 'multiimage'),
        getRow(t('campaignAnalytics.singleImage'), 'singleimage')
      ]),
      section(t('campaignAnalytics.performance'), [
        getRow(t('campaignAnalytics.impressions'), 'impressions'),
        getRow(t('campaignAnalytics.reach'), 'reach'),
        getRow(t('campaignAnalytics.reactions'), 'reactions'),
        getRow(t('campaignAnalytics.comments'), 'comments'),
        getRow(t('campaignAnalytics.shares'), 'shares'),
        getRow(t('campaignAnalytics.interactions'), 'interactions'),
        getRow(t('campaignAnalytics.engagementRate'), 'engagementRate'),
        getRow(t('campaignAnalytics.avgImpressions'), 'avgImpressions'),
        getRow(t('campaignAnalytics.avgReach'), 'avgReach'),
        getRow(t('campaignAnalytics.avgReactions'), 'avgReactions'),
        getRow(t('campaignAnalytics.avgComments'), 'avgComments'),
        getRow(t('campaignAnalytics.avgShares'), 'avgShares'),
        getRow(t('campaignAnalytics.avgInteractions'), 'avgInteractions')
      ]),
      section(t('campaignAnalytics.performanceRating'), [
        getRatingRow('😐', t('campaignAnalytics.neutralRating') + ' 😐'),
        getRatingRow('🎉', t('campaignAnalytics.goodRating') + ' 🎉'),
        getRatingRow('🚀', t('campaignAnalytics.greatRating') + ' 🚀')
      ])
    ]
  };

  return dataTable;
}

export function formatCampaignDataForComponent(
  campaignPosts: Post[],
  selectedCampaigns: Campaign[],
  selectedCampaignPost: string,
  t: any
): ComponentProps {
  const allPosts: MinimalFormattedPostData[] = [];

  campaignPosts.forEach((post) => {
    let campaigns = post.campaigns || [];

    // Filter campaigns if a specific campaign is selected
    if (selectedCampaignPost !== 'all') {
      campaigns = campaigns.filter((c) => c.uuid === selectedCampaignPost);
    }

    const analytics = post?.analytics;
    const publishedPlatforms = analytics.data_as_json?.published || [];

    // Get preview image (fallback if needed)
    let previewImage = post?.preview_image_url || '';

    // Determine platform flags (presence only)
    const platformSet = new Set(
      publishedPlatforms.map((p) => p.platform?.toLowerCase())
    );

    // Fallback safe logo
    const campaignLogo =
      selectedCampaigns.find((c) => c.uuid === campaigns[0]?.uuid)?.logo_url ||
      '';

    const formattedPost: MinimalFormattedPostData = {
      preview_image_url: previewImage,
      text: post?.text || '',

      profile: {
        name: campaigns.map((c) => c.name).join(', '),
        imageUrl: campaignLogo
      },
      publication_date: post.publication_date || '',
      analytics: {
        sum_impressions: analytics?.sum_impressions ?? 0,
        sum_reach: analytics?.sum_reach ?? 0,
        sum_reactions: analytics?.sum_reactions ?? 0,
        sum_comments: analytics?.sum_comments ?? 0,
        sum_shares: analytics?.sum_shares ?? 0,
        sum_engagement: analytics?.sum_engagement ?? 0
      },
      facebook_post: platformSet.has(FACEBOOK),
      instagram_post: platformSet.has(INSTAGRAM),
      twitter_post: platformSet.has(TWITTER),
      linkedin_personal_account_post: platformSet.has(
        LINKEDIN_PERSONAL_ACCOUNT
      ),
      linkedin_business_page_post: platformSet.has(LINKEDIN_BUSINESS_PAGE),
      youtube_post: platformSet.has(YOUTUBE),
      tiktok_business_page_post: platformSet.has(TIKTOK_BUSINESS_PAGE),
      tiktok_personal_account_post: platformSet.has(TIKTOK_PERSONAL_ACCOUNT)
    };

    allPosts.push(formattedPost);
  });

  const sortOptions: SortOption[] = [
    { name: 'engagement', title: t('lomavisCampaignAnalytics.engagement') },
    { name: 'impressions', title: t('lomavisCampaignAnalytics.impressions') },
    { name: 'reactions', title: t('lomavisCampaignAnalytics.reactions') },
    { name: 'comments', title: t('lomavisCampaignAnalytics.comments') },
    { name: 'reach', title: t('lomavisCampaignAnalytics.reach') },
    { name: 'shares', title: t('lomavisCampaignAnalytics.shares') }
  ];

  return {
    title: t('lomavisCampaignAnalytics.best_performing_posts'),
    data: allPosts,
    sortOptions
  };
}

export function collatePostsByTypePerCampaign(
  posts: Post[]
): Array<{ label: string; data: Record<string, number> }> {
  const campaignMap: Record<
    string,
    { label: string; data: Record<string, number> }
  > = {};

  const contentTypeLabels = {
    content_type_is_single_image_post: 'Single image',
    content_type_is_multi_image_post: 'Multi image',
    content_type_is_video_post: 'Video',
    content_type_is_reel_post: 'Reel',
    content_type_is_story_post: 'Story',
    content_type_is_text_post: 'Text'
  };

  posts.forEach((post) => {
    if (!post.campaigns || post.campaigns.length === 0) return;

    Object.entries(contentTypeLabels).forEach(([key, label]) => {
      if (post[key as keyof Post]) {
        post.campaigns.forEach((campaign) => {
          const uuid = campaign.uuid;
          if (!campaignMap[uuid]) {
            campaignMap[uuid] = {
              label: campaign.name,
              data: {
                'Single image': 0,
                'Multi image': 0,
                Video: 0,
                Reel: 0,
                Story: 0,
                Text: 0
              }
            };
          }

          campaignMap[uuid].data[label] += 1;
        });
      }
    });
  });

  // 🔽 Remove any content types that have 0 in ALL campaigns
  const allCounts = Object.values(campaignMap).map((c) => c.data);

  const typesToKeep = Object.keys(contentTypeLabels)
    .map((key) => contentTypeLabels[key])
    .filter((label) => allCounts.some((countObj) => countObj[label] > 0));

  const cleanedResults = Object.values(campaignMap).map(({ label, data }) => {
    const filteredData: Record<string, number> = {};
    typesToKeep.forEach((type) => {
      filteredData[type] = data[type];
    });
    return {
      label,
      data: filteredData
    };
  });

  return cleanedResults;
}

export interface HeatmapDayData {
  title: string; // e.g. "Monday"
  data: Record<number, number>; // { hour: count }
}

export interface CampaignHeatmap {
  name: string;
  data: HeatmapDayData[];
}
export function collatePostsByTimePerCampaign(
  posts: Post[],
  t: any
): CampaignHeatmap[] {
  const days = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday'
  ];

  const campaignMap: Record<
    string,
    { name: string; dayHourCounts: Record<string, Record<number, number>> }
  > = {};

  posts.forEach((post) => {
    const publishedPlatforms = post.analytics?.data_as_json?.published || [];
    const postDate = post.publication_date
      ? new Date(post.publication_date)
      : null;

    if (!postDate) return;

    const day = days[(postDate.getDay() + 6) % 7];
    const hour = postDate.getHours();

    post.campaigns?.forEach((campaign) => {
      const campaignId = campaign.uuid;
      if (!campaignMap[campaignId]) {
        // Initialize day/hour structure
        const dayHourCounts: Record<string, Record<number, number>> = {};
        days.forEach((d) => {
          dayHourCounts[d] = {};
          for (let h = 0; h < 24; h++) {
            dayHourCounts[d][h] = 0;
          }
        });

        campaignMap[campaignId] = {
          name: campaign.name,
          dayHourCounts
        };
      }

      const { dayHourCounts } = campaignMap[campaignId];

      // Count each platform the post is published on as a separate post
      publishedPlatforms.forEach((_) => {
        dayHourCounts[day][hour] += 1;
      });
    });
  });

  // Build final structure
  return Object.values(campaignMap).map((entry) => ({
    name: entry.name,
    data: days.map((day) => ({
      title: day,
      data: entry.dayHourCounts[day]
    }))
  }));
}

export function collateInteractionsByTimePerCampaign(
  posts: Post[]
): CampaignHeatmap[] {
  const days = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday'
  ];

  const campaignMap: Record<
    string,
    { name: string; dayHourCounts: Record<string, Record<number, number>> }
  > = {};

  posts.forEach((post) => {
    const campaigns = post.campaigns || [];
    const publishedPlatforms = post.analytics?.data_as_json?.published || [];

    publishedPlatforms.forEach((platformObj) => {
      const platform = platformObj.platform?.toLowerCase();
      if (!platform) return;

      const platformAnalytics =
        post.analytics?.data_as_json?.overview?.[platform] || {};
      const interactions = platformAnalytics.interactions ?? 0;

      const date = new Date(post.publication_date || post.created);
      const day = days[(date.getDay() + 6) % 7]; // Adjust for week starting on Monday
      const hour = date.getHours();

      campaigns.forEach((campaign) => {
        const campaignId = campaign.uuid;
        const campaignName = campaign.name;

        // Initialize bucket if needed
        if (!campaignMap[campaignId]) {
          const dayHourCounts: Record<string, Record<number, number>> = {};
          days.forEach((d) => {
            dayHourCounts[d] = {};
            for (let h = 0; h < 24; h++) {
              dayHourCounts[d][h] = 0;
            }
          });

          campaignMap[campaignId] = {
            name: campaignName,
            dayHourCounts
          };
        }

        const { dayHourCounts } = campaignMap[campaignId];
        dayHourCounts[day][hour] += interactions;
      });
    });
  });

  // Convert to array format expected by component
  const result: CampaignHeatmap[] = Object.values(campaignMap).map(
    ({ name, dayHourCounts }) => {
      const data: HeatmapDayData[] = days.map((day) => ({
        title: day,
        data: dayHourCounts[day]
      }));
      return { name, data };
    }
  );

  return result;
}
